/**
 * O3 — Telemetry feed event formatter.
 *
 * Translates a `RenderEvent` from the existing event stream into a
 * structured line for the overlay's scrolling event log. Each line
 * carries enough fields for the renderer to color + group rows
 * without pattern-matching on `kind` again:
 *
 *   - `tick`      — for the time tag
 *   - `side`      — "A" | "B" | "—"  (— for events without a clear side)
 *   - `verb`      — ALL-CAPS short label (HIT, KILL, CRIT, ...)
 *   - `detail`    — readable predicate text ("L_SIDE −8 ballistic")
 *   - `severity`  — "info" | "warn" | "alert" — for token-driven row tint
 *
 * Unknown event kinds collapse to severity "info" + verb "EVENT".
 *
 * Pure transform — no React. Storybook fixtures + the wired overlay
 * both feed through here so visual + tests share one source of truth.
 */

import type { RenderEvent, Damage } from "../replay/events";

export type TelemetrySeverity = "info" | "warn" | "alert";

export interface TelemetryLine {
  /** Stable line id for React keys (tick + array index from the
   *  caller's slice — we don't manage IDs here). */
  tick: number;
  side: "A" | "B" | "—";
  verb: string;
  detail: string;
  severity: TelemetrySeverity;
}

function damageNoun(d: Damage): string {
  switch (d) {
    case "ballistic":
    case "energy":
    case "explosive":
    case "missile":
      return d;
    default:
      return "";
  }
}

/** Format one normalized event. */
export function formatEvent(e: RenderEvent): TelemetryLine {
  switch (e.kind) {
    case "weapon_fire":
      return {
        tick: e.tick,
        side: e.shooter,
        verb: "FIRE",
        detail: `tool ${e.tool} ${damageNoun(e.damageType)}`.trim(),
        severity: "info",
      };
    case "weapon_miss":
      return {
        tick: e.tick,
        side: e.shooter,
        verb: "MISS",
        detail: `tool ${e.tool} ${damageNoun(e.damageType)}`.trim(),
        severity: "info",
      };
    case "hit": {
      const dmgNoun = damageNoun(e.damageType);
      const detail = dmgNoun
        ? `${e.zone} −${e.damage} ${dmgNoun}`
        : `${e.zone} −${e.damage}`;
      return {
        tick: e.tick,
        side: e.victim,
        verb: "HIT",
        detail,
        severity: e.damage >= 10 ? "warn" : "info",
      };
    }
    case "kill":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "KILL",
        detail: e.cause,
        severity: "alert",
      };
    case "stagger":
      return { tick: e.tick, side: e.victim, verb: "STAGGER", detail: "", severity: "warn" };
    case "knockdown":
      return { tick: e.tick, side: e.victim, verb: "DOWN", detail: "knockdown", severity: "warn" };
    case "shutdown":
      return { tick: e.tick, side: e.victim, verb: "SHUTDOWN", detail: "heat overflow", severity: "alert" };
    case "restart":
      return { tick: e.tick, side: e.victim, verb: "RESTART", detail: "back online", severity: "info" };
    case "ammo_explosion":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "COOKOFF",
        detail: "ammo cookoff",
        severity: "alert",
      };
    case "exposed":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "BREACH",
        detail: `${e.zone} exposed`,
        severity: "alert",
      };
    case "crit_engine":
      return { tick: e.tick, side: e.victim, verb: "CRIT", detail: "engine", severity: "warn" };
    case "crit_gyro":
      return { tick: e.tick, side: e.victim, verb: "CRIT", detail: "gyro", severity: "warn" };
    case "crit_actuator":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "CRIT",
        detail: `${e.zone} actuator`,
        severity: "warn",
      };
    case "crit_tool_destroyed":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "CRIT",
        detail: `tool ${e.tool} destroyed`,
        severity: "warn",
      };
    case "ams_cancel":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "AMS",
        detail: "salvo intercepted",
        severity: "info",
      };
    case "collision":
      return { tick: e.tick, side: "—", verb: "COLLIDE", detail: "chassis contact", severity: "info" };
    case "ghost_heat":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "GHOST",
        detail: "ghost-heat penalty",
        severity: "warn",
      };
    case "flush_coolant":
      return {
        tick: e.tick,
        side: e.victim,
        verb: "FLUSH",
        detail: "coolant flush",
        severity: "info",
      };
    case "terrain_change":
      return {
        tick: e.tick,
        side: "—",
        verb: "TERRAIN",
        detail: `(${e.position.q},${e.position.r}) ${e.from}→${e.to}`,
        severity: "info",
      };
    case "los_blocked":
      return {
        tick: e.tick,
        side: e.shooter,
        verb: "LOS",
        detail: "line-of-sight blocked",
        severity: "info",
      };
    case "other":
    default:
      return { tick: e.tick, side: "—", verb: "EVENT", detail: "", severity: "info" };
  }
}

/**
 * Format every event in a stream up to (and including) `tick`, newest
 * first, optionally capped to `maxRows`. Caller owns the slice — the
 * TelemetryFeed component uses this to derive the visible rows on
 * every tick change.
 *
 * Kinds in `excludeKinds` are filtered out (default excludes
 * weapon_fire / weapon_miss — they're spammy and the visible scene
 * already telegraphs them via tracers).
 */
export function telemetryRows(
  stream: ReadonlyArray<RenderEvent>,
  tick: number,
  maxRows = 8,
  excludeKinds: ReadonlySet<RenderEvent["kind"]> = DEFAULT_EXCLUDE,
): TelemetryLine[] {
  const out: TelemetryLine[] = [];
  for (let i = stream.length - 1; i >= 0 && out.length < maxRows; i--) {
    const e = stream[i];
    if (e.tick > tick) continue;
    if (excludeKinds.has(e.kind)) continue;
    out.push(formatEvent(e));
  }
  return out;
}

const DEFAULT_EXCLUDE: ReadonlySet<RenderEvent["kind"]> = new Set<RenderEvent["kind"]>([
  "weapon_fire",
  "weapon_miss",
  "other",
]);
